/**
 * Tab Dier (keuze 14, ontwerp 31 punt 4 tot 7; sessie 141-E volgens mock
 * v10). Per dier, met bladeren "1 van 2" (of vegen op de iPhone):
 * pootafdruk of foto, naam groot met een pil "naam bevestigd" of "naam
 * waarschijnlijk" (met bron), de kopregel soort · ras · geslacht · leeftijd
 * · fase, en direct daaronder één bronregel voor ras, leeftijd en geslacht
 * (keuze 17: niet nog eens als losse feiten). Daarna de feiten in de
 * volgorde van de mock (Gewicht, Conditie, Beweging, Vermijden, Dierenarts,
 * Raskaart) met een pil die status, bron en datum in woorden zegt;
 * Voeding nodig tegenover werkelijk met de conclusie; de Herbestellen-meter
 * van dit dier; het Dossier compact (velden en de volgende vraag, de rest
 * achter "Alle velden"); en wat de klant zelf vertelde.
 *
 * Sessie 141-E: wat de klant later anders noemde (A1, nooit overschreven)
 * staat als "klant noemde later: ..." met "Overnemen" en "Laten": één klik.
 * Heeft een kopfeit (ras, leeftijd, geslacht, naam) een voorstel, dan krijgt
 * het alsnog een eigen feitregel: de bevestigde waarde blijft staan en het
 * voorstel staat eronder (A1). Zonder voorstel blijft keuze 17 gelden.
 *
 * Bewerken gaat via het bestaande `Bewerkvlak` en "+ Dier" via
 * `NieuwDier`: geen nieuwe editor. Onbekend staat er als "onbekend".
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef, useState, type TouchEvent } from "react";

import { v6Api } from "../../api";
import { Icoon } from "../../Icoon";
import { v6Sleutels } from "../../sleutels";
import { Bewerkvlak, Dossier, NieuwDier, UitGesprekken } from "../../Klanten/kaart";
import { useAfbeelding } from "../../Klanten/kaart/smal";
import {
  castratieWoord,
  datumKort,
  geslachtWoord,
  getal,
  leeftijdWoord,
} from "../../Klanten/opmaak";
import type {
  KaartDier,
  KaartDossier,
  KaartFeit,
  KaartMeter,
  KaartVermijden,
  KlantKaart,
} from "../../Klanten/types";
import { dagDatum, maandJaarKort } from "./hulp";
import { Pootafdruk } from "./Pootafdruk";

const SWIPE_PX = 40;

const FASE: Record<string, string> = {
  pup: "pup",
  jong: "jong",
  volwassen: "volwassen",
  senior: "senior",
};

function Onbekend({ tekst = "onbekend" }: { tekst?: string }) {
  return <span className="kk10-onbekend">{tekst}</span>;
}

function Foto({ dier }: { dier: KaartDier }) {
  const { src } = useAfbeelding(dier.foto_url);
  return (
    <span className="kk10-dav">
      {src ? <img src={src} alt={dier.naam} /> : <Pootafdruk />}
    </span>
  );
}

/** "bevestigd" of "waarschijnlijk": de stand in woorden (nooit alleen kleur). */
function standWoord141(status: KaartFeit["status"]): string | null {
  if (status === "bevestigd") return "bevestigd";
  if (status === "aanname") return "waarschijnlijk";
  return null;
}

/** Bron en datum kort: "klant, chat" en 14 mrt 2025 wordt "klant chat 14 mrt 2025". */
function bronKort(bron: string | null | undefined, datum: string | null | undefined): string {
  return [bron?.replace(/,\s*/g, " "), datumKort(datum ?? null)].filter(Boolean).join(" ");
}

/** De pil zoals in de mock: "bevestigd, klant chat 14 mrt 2025". */
function Statuspil({ feit, voor = "" }: { feit: KaartFeit; voor?: string }) {
  const stand = standWoord141(feit.status);
  if (!stand || feit.waarde === null) return null;
  const bron = bronKort(feit.bron, feit.datum);
  return (
    <span className={`kk10-pil ${feit.status === "bevestigd" ? "zeker" : "waars"}`}>
      {`${voor}${stand}${bron ? `, ${bron}` : ""}`}
    </span>
  );
}

/** "klant noemde later: Beagle, WhatsApp, 26 okt" met Overnemen en Laten. */
function Voorstel({
  feit,
  dierId,
  klantId,
  metLabel = false,
}: {
  feit: KaartFeit;
  dierId: string;
  klantId: string;
  metLabel?: boolean;
}) {
  const queryClient = useQueryClient();
  const sleutel = feit.voorstel_sleutel ?? null;
  const beslis = useMutation({
    mutationFn: (actie: "overnemen" | "laten") =>
      v6Api.huisdierVoorstel(dierId, sleutel as string, actie),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: v6Sleutels.klantKaart(klantId) }),
  });
  if (!feit.voorstel) return null;
  const herkomst = [
    // "klant, WhatsApp" wordt "WhatsApp": de regel zegt al dat het de klant was.
    feit.voorstel_bron?.replace(/^klant,\s*/, ""),
    datumKort(feit.voorstel_datum ?? null),
  ]
    .filter(Boolean)
    .map((d) => `, ${d}`)
    .join("");
  return (
    <span className="kk10-voorstel" data-voorstel={feit.veld}>
      <small className="kk10-bron">
        {metLabel ? <b>{`${feit.label}: `}</b> : null}
        {`klant noemde later: ${feit.voorstel}${herkomst}`}
      </small>
      {sleutel ? (
        <span className="kk10-voorstelknoppen">
          <button
            type="button"
            className="kk10-tekstknop"
            disabled={beslis.isPending}
            aria-label={`${feit.label}: ${feit.voorstel} overnemen`}
            onClick={() => beslis.mutate("overnemen")}
          >
            Overnemen
          </button>
          <button
            type="button"
            className="kk10-tekstknop stil"
            disabled={beslis.isPending}
            aria-label={`${feit.label}: voorstel ${feit.voorstel} laten`}
            onClick={() => beslis.mutate("laten")}
          >
            Laten
          </button>
        </span>
      ) : null}
      {beslis.isError ? (
        <small className="v6-fout" role="alert">
          Lukte niet. Ververs de kaart of gebruik Bewerken.
        </small>
      ) : null}
    </span>
  );
}

function Feit({ feit, dierId, klantId }: { feit: KaartFeit; dierId: string; klantId: string }) {
  return (
    <>
      <span className="l">{feit.label}</span>
      <span data-veld={feit.veld}>
        {feit.waarde !== null ? (
          feit.waarde
        ) : (
          <Onbekend
            tekst={feit.status === "nog_niet_bijgehouden" ? "nog niet bijgehouden" : "onbekend"}
          />
        )}
        <Statuspil feit={feit} />
        <Voorstel feit={feit} dierId={dierId} klantId={klantId} />
      </span>
    </>
  );
}

function standWoord(v: KaartVermijden): string {
  if (v.stand === "wens") return "wens";
  if (v.stand === "intolerantie") return "intolerantie volgens klant";
  return v.vastgesteld_dierenarts ? "allergie, vastgesteld door dierenarts" : "allergie";
}

/** "4 jaar (geboren apr 2022)"; geschat: "(geboren ca. 2022)". */
function leeftijdRegel(dier: KaartDier): string {
  const leeftijd = leeftijdWoord(dier.leeftijd_maanden);
  if (!leeftijd) return "leeftijd onbekend";
  if (!dier.geboortedatum) return leeftijd;
  const geboren = dier.geboortedatum_geschat
    ? `geboren ca. ${dier.geboortedatum.slice(0, 4)}`
    : `geboren ${maandJaarKort(dier.geboortedatum)}`;
  return `${leeftijd} (${geboren})`;
}

const KOP_LABEL: Record<string, string> = {
  ras: "ras",
  leeftijd: "leeftijd",
  geslacht: "geslacht",
};

/**
 * Eén bronregel voor de kopregel: "ras en leeftijd bevestigd, klant mail
 * 8 sep · geslacht waarschijnlijk, uit bestellingen". Alleen velden met
 * een waarde; wat onbekend is zegt de kopregel al.
 */
function kopBron(kop: KaartFeit[]): string | null {
  const groepen = new Map<string, { velden: string[]; stand: string; bron: string }>();
  for (const f of kop) {
    const stand = standWoord141(f.status);
    if (!KOP_LABEL[f.veld] || f.waarde === null || !stand) continue;
    const bron = bronKort(f.bron, f.datum);
    const sleutel = `${stand}|${bron}`;
    const groep = groepen.get(sleutel) ?? { velden: [], stand, bron };
    groep.velden.push(KOP_LABEL[f.veld]);
    groepen.set(sleutel, groep);
  }
  if (groepen.size === 0) return null;
  return [...groepen.values()]
    .map((g) => {
      const velden =
        g.velden.length > 1
          ? `${g.velden.slice(0, -1).join(", ")} en ${g.velden[g.velden.length - 1]}`
          : g.velden[0];
      return `${velden} ${g.stand}${g.bron ? `, ${g.bron}` : ""}`;
    })
    .join(" · ");
}

function Naampil({ naam }: { naam: KaartFeit | undefined }) {
  if (!naam) return null;
  if (naam.waarde === null || !standWoord141(naam.status)) {
    return (
      <span className="kk10-pil waars" data-testid="naam-pil">
        naam onbekend
      </span>
    );
  }
  return (
    <span data-testid="naam-pil">
      <Statuspil feit={naam} voor="naam " />
    </span>
  );
}

function Dierblok({
  dier,
  klantId,
  onBewerk,
}: {
  dier: KaartDier;
  klantId: string;
  onBewerk?: () => void;
}) {
  const fase = dier.groeifase ? FASE[dier.groeifase] : null;
  const geslacht = geslachtWoord(dier.diersoort, dier.geslacht);
  const castratie = castratieWoord(dier.castratie, dier.geslacht);
  const kop = dier.kop_feiten ?? [];
  const naam = kop.find((f) => f.veld === "naam");
  const bronregel = kopBron(kop);
  // Kopfeiten met een voorstel krijgen een feitregel; de rest heeft geen eigen regel.
  const kopMetVoorstel = kop.filter((f) => f.voorstel);
  const voorstellen = (dier.overige_voorstellen ?? []).filter((f) => f.voorstel);
  // Mock-volgorde: Vermijden na Beweging, voor Dierenarts.
  const feiten = dier.feiten;
  const knip = feiten.findIndex((f) => f.veld === "dierenarts");
  const voor = knip < 0 ? feiten : feiten.slice(0, knip);
  const na = knip < 0 ? [] : feiten.slice(knip);
  return (
    <div className={`kk10-blok kk10-dier${dier.is_overleden ? " overleden" : ""}`}>
      <div className="kop">
        <Foto dier={dier} />
        <div className="wie">
          <div className="dn">
            <span className="groot">{dier.naam}</span>
            <Naampil naam={naam} />
            {dier.is_overleden ? (
              <span className="kk10-pil">
                {dier.overleden_op ? `overleden ${datumKort(dier.overleden_op)}` : "overleden"}
              </span>
            ) : null}
            {onBewerk ? (
              <button type="button" className="kk10-tekstknop" onClick={onBewerk}>
                Bewerken
              </button>
            ) : null}
          </div>
          <div className="dr" data-testid="dier-regel">
            {[
              dier.diersoort,
              dier.ras ?? "ras onbekend",
              [geslacht, castratie].filter(Boolean).join(", ") || "geslacht onbekend",
              leeftijdRegel(dier),
              fase ?? "fase onbekend",
            ].join(" · ")}
          </div>
          {bronregel ? (
            <small className="kk10-kopbron" data-testid="kop-bron">
              {bronregel}
            </small>
          ) : null}
        </div>
      </div>
      {voorstellen.length > 0 ? (
        <div className="kk10-voorstellen" data-testid="voorstellen">
          {voorstellen.map((f) => (
            <Voorstel key={`v-${f.veld}`} feit={f} dierId={dier.id} klantId={klantId} metLabel />
          ))}
        </div>
      ) : null}
      <div className="kk10-feiten">
        {dier.herkomst ? <Feit feit={dier.herkomst} dierId={dier.id} klantId={klantId} /> : null}
        {kopMetVoorstel.map((f) => (
          <Feit key={`kop-${f.veld}`} feit={f} dierId={dier.id} klantId={klantId} />
        ))}
        {voor.map((f) => (
          <Feit key={f.veld} feit={f} dierId={dier.id} klantId={klantId} />
        ))}
        <span className="l">Vermijden</span>
        <span data-veld="vermijden">
          {dier.vermijden.length === 0 ? (
            <Onbekend tekst="niets bekend" />
          ) : (
            dier.vermijden.map((v, i) => (
              <span key={v.allergeen}>
                {i > 0 ? ", " : null}
                {v.allergeen} <small className="kk10-bron">{standWoord(v)}</small>
              </span>
            ))
          )}
        </span>
        {na.map((f) => (
          <Feit key={f.veld} feit={f} dierId={dier.id} klantId={klantId} />
        ))}
      </div>
    </div>
  );
}

const EET: Record<string, string> = {
  hoofdvoer: "Hoofdvoer",
  natvoer: "Natvoer",
  snacks: "Snacks",
  worst: "Worst",
};

function Voeding({ dier }: { dier: KaartDier }) {
  const { nodig, nodig_reden, werkelijk, werkelijk_reden, conclusie } = dier.voeding;
  const eet = dier.eet.filter((e) => e.product_naam);
  return (
    <div className="kk10-blok" data-testid="voeding">
      <div className="bh">
        Voeding<span className="r">nodig tegenover werkelijk</span>
      </div>
      <div className="bb">
        {/* Sessie 188: bij afvallen is "nodig" het startpunt op het streefgewicht. */}
        <span className="l">{nodig?.afvallen ? "Nodig om af te vallen" : "Nodig"}</span>
        <span>
          {nodig ? (
            <>
              <span className="num">{`${getal(nodig.kcal_per_dag)} kcal, ${getal(nodig.gram_per_dag)} g per dag`}</span>
              <small className="kk10-bron">{nodig.op_basis_van}</small>
            </>
          ) : (
            <Onbekend tekst={nodig_reden ? `onbekend: ${nodig_reden}` : "onbekend"} />
          )}
        </span>
        <span className="l">Werkelijk</span>
        <span>
          {werkelijk ? (
            <>
              <span className="num">{`ongeveer ${getal(werkelijk.gram_per_dag)} g per dag`}</span>
              <small className="kk10-bron">{werkelijk.op_basis_van}</small>
            </>
          ) : (
            <Onbekend tekst={werkelijk_reden ? `onbekend: ${werkelijk_reden}` : "onbekend"} />
          )}
        </span>
        {conclusie ? (
          <span className="vol kk10-concl" data-testid="conclusie">
            {conclusie.tekst}
          </span>
        ) : null}
        {eet.map((e) => (
          <span key={e.soort} className="vol kk10-eet">
            <span className="l">{EET[e.soort] ?? e.soort}</span> {e.product_naam}
            {e.frequentie ? <span className="kk10-bron"> · {e.frequentie}</span> : null}
          </span>
        ))}
      </div>
    </div>
  );
}

function Meter({ meter, seintje }: { meter: KaartMeter; seintje: string | null }) {
  const zak =
    meter.zak_gram !== null
      ? `${meter.aantal > 1 ? `${meter.aantal} x ` : ""}${getal(meter.zak_gram / 1000, meter.zak_gram % 1000 ? 1 : 0)} kg`
      : null;
  const procent = meter.procent_op;
  return (
    <div className="kk10-blok" data-testid="herbestellen">
      <div className="bh">
        Herbestellen
        <span className="r">
          {meter.leeg_op ? (
            <>
              leeg rond <b className="num">{dagDatum(meter.leeg_op)}</b>
            </>
          ) : (
            "leeg-datum onbekend"
          )}
        </span>
      </div>
      <div className="bb">
        <span className="vol">
          {meter.product_naam}
          {zak && !meter.product_naam.includes(zak) ? `, ${zak}` : ""}
          {`, ${meter.start_bron} ${dagDatum(meter.start_op)}`}
        </span>
        {procent !== null ? (
          <>
            <span className="vol">
              <span
                className="kk10-meter"
                role="meter"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={procent}
                aria-label={`${procent} procent op`}
              >
                <span style={{ width: `${procent}%` }} />
              </span>
            </span>
            <span className="l">Vandaag</span>
            <span className="num">
              {`dag ${meter.dag ?? "?"} van ${meter.dagen_totaal ?? "?"}, ${procent}% op`}
              {meter.kg_over !== null ? `, nog ${getal(meter.kg_over, 1)} kg` : ""}
            </span>
          </>
        ) : (
          <span className="vol">
            <Onbekend tekst={meter.reden ? `onbekend: ${meter.reden}` : "onbekend"} />
          </span>
        )}
        <span className="l">Seintje</span>
        <span>{seintje ?? <Onbekend tekst="niet gevraagd" />}</span>
      </div>
    </div>
  );
}

function seintjeWoord(kaart: KlantKaart): string | null {
  const s = kaart.toestemmingen?.seintje;
  if (!s || !s.stand) return null;
  const delen = [s.stand, s.kanaal, s.gegeven_op || s.gevraagd_op ? `gevraagd ${datumKort(s.gegeven_op ?? s.gevraagd_op)}` : null];
  return delen.filter(Boolean).join(", ");
}

/** De meters van dit dier. Een meter zonder (bekend) dier staat bij het
 *  eerste levende dier, zodat er nooit een meter van de kaart valt. */
function metersVan(kaart: KlantKaart, dier: KaartDier): KaartMeter[] {
  const levend = kaart.dieren.filter((d) => !d.is_overleden);
  const namen = new Set(levend.map((d) => d.naam));
  const eerste = levend[0]?.id === dier.id;
  return kaart.herbestellen.meters.filter(
    (m) => (m.dier_naam && m.dier_naam === dier.naam) || (eerste && !namen.has(m.dier_naam ?? "")),
  );
}

/**
 * Dossier zoals de mock: "7 van 10 velden" en de volgende vraag (hooguit één
 * per contact). Wat al bekend is staat al op de kaart (keuze 17); de open
 * velden met Gevraagd en Wil niet delen staan achter "Alle velden".
 */
function DossierKort({ klantId, dossier }: { klantId: string; dossier: KaartDossier }) {
  const [open, setOpen] = useState(false);
  const volgende =
    dossier.nog_te_vragen.find((v) => v.eerstvolgende) ?? dossier.nog_te_vragen[0] ?? null;
  return (
    <div className="kk10-blok" data-testid="dossier">
      <div className="bh">
        Dossier
        <span className="r num">{`${dossier.velden_bekend} van ${dossier.velden_totaal} velden`}</span>
      </div>
      <div className="bb">
        <span className="l">Volgende vraag</span>
        <span data-testid="volgende-vraag">
          {volgende ? (
            <>
              {volgende.label}
              {volgende.huisdier_naam ? ` (${volgende.huisdier_naam})` : ""}
              <small className="kk10-bron"> · hooguit één per contact</small>
            </>
          ) : (
            <Onbekend tekst="geen open vragen" />
          )}
        </span>
        <span className="vol">
          <button
            type="button"
            className="kk10-tekstknop"
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
          >
            {open ? "Minder tonen" : "Alle velden"}
          </button>
        </span>
      </div>
      {open ? (
        <div className="kk10-in">
          <Dossier klantId={klantId} dossier={dossier} compact />
        </div>
      ) : null}
    </div>
  );
}

export function TabDier({ kaart, onCopiloot }: { kaart: KlantKaart; onCopiloot?: () => void }) {
  const [index, setIndex] = useState(0);
  const [bewerk, setBewerk] = useState<string | null>(null);
  const [nieuw, setNieuw] = useState(false);
  const start = useRef<{ x: number; y: number } | null>(null);
  const dieren = kaart.dieren;
  const aantal = dieren.length;
  const nu = Math.min(index, Math.max(0, aantal - 1));
  const dier = dieren[nu] ?? null;

  const naar = (i: number) => {
    setBewerk(null);
    setIndex(Math.max(0, Math.min(aantal - 1, i)));
  };
  const opStart = (e: TouchEvent) => {
    const t = e.touches[0];
    start.current = t ? { x: t.clientX, y: t.clientY } : null;
  };
  const opEind = (e: TouchEvent) => {
    const t = e.changedTouches[0];
    const begin = start.current;
    start.current = null;
    if (!t || !begin) return;
    const dx = t.clientX - begin.x;
    if (Math.abs(t.clientY - begin.y) > Math.abs(dx)) return;
    if (dx <= -SWIPE_PX) naar(nu + 1);
    else if (dx >= SWIPE_PX) naar(nu - 1);
  };

  const meters = dier ? metersVan(kaart, dier) : [];
  const seintje = seintjeWoord(kaart);

  return (
    <>
      <div className="kk10-dierkop">
        {aantal > 1 ? (
          <span className="kk10-blader">
            <button
              type="button"
              aria-label="Vorig dier"
              disabled={nu === 0}
              onClick={() => naar(nu - 1)}
            >
              <Icoon naam="terug" klein />
            </button>
            <span className="num" aria-live="polite">{`${nu + 1} van ${aantal}`}</span>
            <button
              type="button"
              aria-label="Volgend dier"
              disabled={nu === aantal - 1}
              onClick={() => naar(nu + 1)}
            >
              <Icoon naam="terug" klein className="om" />
            </button>
          </span>
        ) : (
          <span className="stil">
            {aantal === 0 ? "Geen dier bekend" : (kaart.dossier?.andere_dieren ?? "1 dier")}
          </span>
        )}
        <button type="button" className="kk10-tekstknop" onClick={() => setNieuw(true)}>
          + Dier
        </button>
      </div>
      {nieuw ? (
        <NieuwDier
          klantId={kaart.id}
          onSluit={() => setNieuw(false)}
          onGemaakt={() => setNieuw(false)}
        />
      ) : null}

      {dier ? (
        <div className="kk10-swipe" onTouchStart={opStart} onTouchEnd={opEind} data-testid="dier-swipe">
          <Dierblok
            key={dier.id}
            dier={dier}
            klantId={kaart.id}
            onBewerk={() => setBewerk(dier.id)}
          />
          {bewerk === dier.id ? (
            <Bewerkvlak key={bewerk} klantId={kaart.id} dier={dier} onSluit={() => setBewerk(null)} />
          ) : null}
          {!dier.is_overleden ? <Voeding dier={dier} /> : null}
          {!dier.is_overleden
            ? meters.map((m) => (
                <Meter key={`${m.product_naam}-${m.start_op}`} meter={m} seintje={seintje} />
              ))
            : null}
          {!dier.is_overleden && meters.length === 0 ? (
            <div className="kk10-blok" data-testid="herbestellen">
              <div className="bh">Herbestellen</div>
              <div className="bb">
                <span className="vol">
                  <Onbekend
                    tekst={`geen voorraadmeter: ${kaart.cijfers.herbestelling_reden ?? "nog te weinig bestellingen van hetzelfde voer"}`}
                  />
                </span>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {kaart.dossier ? <DossierKort klantId={kaart.id} dossier={kaart.dossier} /> : null}
      {kaart.uit_gesprekken.length > 0 ? (
        <div className="kk10-blok">
          <div className="bh">Uit gesprekken</div>
          <div className="kk10-in">
            <UitGesprekken feiten={kaart.uit_gesprekken} klantId={kaart.id} onCopiloot={onCopiloot} />
          </div>
        </div>
      ) : null}
    </>
  );
}
